import {
  AlertDialog,
  CirclePlusIcon,
  Color,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { useState } from "react";
import { makeStyles } from "@material-ui/styles";
import { CustomerContracts } from "./components/customer-contracts";
import { ConsultantContracts } from "./components/consultant-contracts";
import {
  ConsultantBookingContractEditorLocation,
  ConsultantContractEditorLocation
} from "@navigation";
import {
  CONSULTANT_BOOKING_CONTRACT_SLIDER,
  CONSULTANT_CONTRACT_SLIDER
} from "@navigation/locations";
import { useGlobalStore, useLocale } from "@hooks";
import { Button, ButtonGroup, Tooltip } from "@material-ui/core";
import { ActionUnion } from "./types";
import {
  GetConsultantContracts,
  useAddUnsignedConsultantContractMutation,
  useGetConsultantContractsPersonalDataQuery,
  usePublishConsultantContractMutation
} from "@graphql";
import { ConsultantContractType } from "@bonliva-crm/shared";
import { ThemeType } from "@theme";

type Props = {
  consultantId: string;
};

type AlertDialog = {
  title: string;
  content?: string;
  onOk: () => void;
  onClose: () => void;
};

export const ContractTab = (props: Props) => {
  const [contractType, setContractType] = useState<"consultant" | "customer">(
    "consultant"
  );
  const [alertDialog, setAlertDialog] = useState<AlertDialog>(null);
  const classes = useStyles();
  const store = useGlobalStore();
  const { showError, showSuccess } = useSnackbarsPipeline();
  const { locale } = useLocale();

  const { data: consultantData } = useGetConsultantContractsPersonalDataQuery({
    variables: {
      id: props.consultantId
    },
    onError: (e) => {
      showError(locale.ErrorOccurred);
      console.error(e);
    },
    fetchPolicy: "cache-first"
  });

  const [publishConsultantContract, { loading: publishConsultantContractLoading }] =
    usePublishConsultantContractMutation({
      onError: (e) => {
        showError(locale.ErrorOccurred);
        console.error(e);
      },
      onCompleted: (data) => {
        window.open(data.publishContract.contractUrl, "_blank");
        showSuccess(locale.Saved);
        setAlertDialog(null);
      }
    });

  const [addConsultantContract, { loading: addConsultantContractLoading }] =
    useAddUnsignedConsultantContractMutation({
      onError: (e) => {
        showError(locale.ErrorOccurred);
        console.error(e);
      },
      onCompleted: (data) => {
        publishConsultantContract({
          variables: {
            contractId: data.addUnSignConsultantContract._id
          }
        });
      },
      refetchQueries: [GetConsultantContracts.OperationName]
    });

  const handleNavigationAction = (a: ActionUnion, contractId?: string) => {
    let action: "add" | "edit" | "forConfirmation" | "duplicate";

    if (a === locale.AddBookingConfirm) {
      action = "forConfirmation";
    } else if (a === locale.Edit) {
      action = "edit";
    } else if (a === "add") {
      action = "add";
    } else if (a === locale.Duplicate) {
      action = "duplicate";
    } else {
      throw new Error("Invalid action type");
    }

    const locationName =
      contractType === "consultant"
        ? CONSULTANT_CONTRACT_SLIDER
        : CONSULTANT_BOOKING_CONTRACT_SLIDER;

    const locationInstance =
      contractType === "consultant"
        ? new ConsultantContractEditorLocation(props.consultantId, contractId, action)
        : new ConsultantBookingContractEditorLocation(
            props.consultantId,
            contractId,
            action
          );

    store.navigation.open(locationName, locationInstance);
  };

  const canCreateContractCheck =
    contractType === "consultant" &&
    consultantData?.consultantById.canCreateContractCheck.length > 0
      ? `${locale.missing}: ${consultantData.consultantById.canCreateContractCheck.join(
          ", "
        )}`
      : "";

  const isSelfInvoicingButtonVisible =
    contractType === "consultant" &&
    consultantData?.consultantById.LegalPersonType === "Subcontractor";

  const canCreateSelfInvoicingContractCheck =
    consultantData?.consultantById.canCreateSelfInvoicingContractCheck;

  let canCreateSelfInvoicingContractTooltip = "";

  if (canCreateSelfInvoicingContractCheck?.length > 0) {
    const parts = [];
    parts.push(locale.ConsultantContractMissingInfo);

    for (const check of canCreateSelfInvoicingContractCheck) {
      parts.push(locale.consultantContractSelfInvoicingCheck[check]);
    }

    canCreateSelfInvoicingContractTooltip = parts.join("\n");
  }

  return (
    <>
      <div className={classes.topWrapper}>
        <ButtonGroup color="primary">
          <Button
            variant={contractType === "consultant" ? "contained" : "outlined"}
            onClick={() => setContractType("consultant")}
          >
            {locale.ConsultantContracts}
          </Button>
          <Button
            variant={contractType === "customer" ? "contained" : "outlined"}
            onClick={() => setContractType("customer")}
          >
            {locale.CustomerContracts}
          </Button>
        </ButtonGroup>
        <div>
          {isSelfInvoicingButtonVisible && (
            <Tooltip
              title={
                canCreateSelfInvoicingContractTooltip ? (
                  <span style={{ whiteSpace: "pre-line" }}>
                    {canCreateSelfInvoicingContractTooltip}
                  </span>
                ) : (
                  ""
                )
              }
            >
              <span>
                <Button
                  className={classes.selfInvoicingButton}
                  variant="contained"
                  onClick={() => {
                    setAlertDialog({
                      title: locale.NewConsultantSelfInvoicingContract,
                      content: locale.NewConsultantSelfInvoicingContractAlert,
                      onOk: () => {
                        addConsultantContract({
                          variables: {
                            consultantId: props.consultantId,
                            data: {
                              ContractType: ConsultantContractType.SelfInvoicing
                            }
                          }
                        });
                      },
                      onClose: () => setAlertDialog(null)
                    });
                  }}
                  disabled={!!canCreateSelfInvoicingContractTooltip}
                >
                  <CirclePlusIcon className={classes.circlePlusIconRed} />
                  {locale.NewConsultantSelfInvoicingContract}
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title={canCreateContractCheck}>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleNavigationAction("add")}
                disabled={!!canCreateContractCheck}
              >
                <CirclePlusIcon className={classes.circlePlusIcon} />
                {contractType === "consultant"
                  ? locale.NewConsultantContract
                  : locale.NewCustomerContract}
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
      {contractType === "consultant" ? (
        <ConsultantContracts
          consultantId={props.consultantId}
          handleNavigationAction={handleNavigationAction}
        />
      ) : (
        <CustomerContracts
          consultantId={props.consultantId}
          handleNavigationAction={handleNavigationAction}
        />
      )}
      {!!alertDialog && (
        <AlertDialog
          open={!!alertDialog}
          title={alertDialog.title}
          onOk={alertDialog.onOk}
          onClose={alertDialog.onClose}
          loading={addConsultantContractLoading || publishConsultantContractLoading}
        >
          {alertDialog.content}
        </AlertDialog>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: ThemeType) => ({
  circlePlusIcon: {
    color: Color.White,
    marginRight: 5
  },
  circlePlusIconRed: {
    color: Color.PrimaryMain,
    marginRight: 5
  },
  selfInvoicingButton: {
    marginRight: theme.spacing(1)
  },
  topWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16
  }
}));
